import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { updatePageSEO } from '../../utils/seo';

export default function DoNotSell() {
  useEffect(() => {
    updatePageSEO({
      title: 'Do Not Sell My Personal Information | Curtis Waters AI Solutions',
      description: 'Exercise your California privacy rights under CCPA. Request to opt-out of the sale of your personal information. Curtis Waters AI Solutions respects your privacy choices and data protection rights.',
      keywords: 'do not sell, CCPA rights, California privacy, opt-out, personal information, data privacy, consumer rights',
      canonical: `${import.meta.env.VITE_SITE_URL}/do-not-sell`,
      ogTitle: 'Do Not Sell My Personal Information | Curtis Waters AI Solutions',
      ogDescription: 'Exercise your California privacy rights under CCPA. Request to opt-out of the sale of your personal information.',
      ogUrl: `${import.meta.env.VITE_SITE_URL}/do-not-sell`,
    });

    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Do Not Sell My Personal Information
          </h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-600 mb-8">
              Last Updated: January 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your California Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                Under the <strong>California Consumer Privacy Act (CCPA)</strong>, California residents have the right to opt-out of the sale of their personal information. Curtis Waters AI Solutions is committed to respecting your privacy rights and providing you with control over your personal data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What Information May Be Sold</h2>
              <p className="text-gray-700 mb-4">
                We may share certain categories of personal information with third parties for business purposes, which may be considered a "sale" under CCPA:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Identifiers</strong>: Name, email address, phone number, IP address</li>
                <li><strong>Commercial Information</strong>: Products or services purchased, browsing history</li>
                <li><strong>Internet Activity</strong>: Website interactions, search history, engagement data</li>
                <li><strong>Geolocation Data</strong>: General location information</li>
                <li><strong>Professional Information</strong>: Company name, job title, business contact details</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Opt-Out</h2>
              <p className="text-gray-700 mb-4">
                You have the right to opt-out of the sale of your personal information at any time. To exercise this right, please contact us using one of the following methods:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Methods</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-teal-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <a href="mailto:privacy@curtiswaters.ai" className="text-teal-500 hover:text-teal-600 cursor-pointer">
                        privacy@curtiswaters.ai
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-teal-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Phone</p>
                      <a href="tel:+17045551234" className="text-teal-500 hover:text-teal-600 cursor-pointer">
                        (704) 555-1234
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-xl text-teal-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Mailing Address</p>
                      <p className="text-gray-700">
                        Curtis Waters AI Solutions<br />
                        Charlotte, NC 28202<br />
                        United States
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What to Include in Your Request</h2>
              <p className="text-gray-700 mb-4">
                When submitting your opt-out request, please provide the following information to help us process your request efficiently:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Your full name</li>
                <li>Email address associated with your account or interactions</li>
                <li>Phone number (if applicable)</li>
                <li>A clear statement that you wish to opt-out of the sale of your personal information</li>
                <li>Any additional information that will help us verify your identity</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Response Timeline</h2>
              <p className="text-gray-700 mb-4">
                We will acknowledge receipt of your request within <strong>10 business days</strong> and complete your request within <strong>45 days</strong>. If we need additional time (up to 90 days total), we will notify you of the reason and extension period.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">No Discrimination</h2>
              <p className="text-gray-700 mb-4">
                We will not discriminate against you for exercising your CCPA rights. You will not be:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Denied goods or services</li>
                <li>Charged different prices or rates for goods or services</li>
                <li>Provided a different level or quality of goods or services</li>
                <li>Suggested that you may receive a different price or quality of goods or services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Additional Privacy Rights</h2>
              <p className="text-gray-700 mb-4">
                In addition to the right to opt-out of sales, California residents have other privacy rights under CCPA:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Right to Know</strong>: Request information about the personal data we collect, use, and share</li>
                <li><strong>Right to Delete</strong>: Request deletion of your personal information</li>
                <li><strong>Right to Correct</strong>: Request correction of inaccurate personal information</li>
                <li><strong>Right to Limit</strong>: Limit the use and disclosure of sensitive personal information</li>
              </ul>
              <p className="text-gray-700 mb-4">
                To exercise these rights, please refer to our <a href="/privacy-policy" className="text-teal-500 hover:text-teal-600 cursor-pointer">Privacy Policy</a> or contact us using the methods above.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Questions or Concerns</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this opt-out process or our privacy practices, please contact our Privacy Team at <a href="mailto:privacy@curtiswaters.ai" className="text-teal-500 hover:text-teal-600 cursor-pointer">privacy@curtiswaters.ai</a>.
              </p>
            </section>

            <div className="bg-blue-50 rounded-lg p-6 mt-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-information-line text-xl text-blue-500"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Important Notice</h3>
                  <p className="text-gray-700">
                    This page is specifically for California residents exercising their rights under CCPA. For general privacy inquiries or if you are not a California resident, please visit our <a href="/privacy-policy" className="text-teal-500 hover:text-teal-600 cursor-pointer">Privacy Policy</a> page.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
